import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Users, Upload, Send, Wand2, Loader2, X, Trash2, Pencil, Save, Plus,
  Filter, Bell, Clock, Mail, MessageCircle, ChevronRight,
  Link as LinkIcon, Copy, Ban,
} from "lucide-react";

const STAGE_STYLES = {
  new: "bg-slate-100 text-slate-800",
  contacted: "bg-sky-100 text-sky-800",
  nurturing: "bg-amber-100 text-amber-800",
  qualified: "bg-violet-100 text-violet-800",
  converted: "bg-emerald-100 text-emerald-800",
  lost: "bg-red-100 text-red-800",
};

const FOLLOWUP_LABEL = { 1: "3-day", 2: "7-day", 3: "14-day+" };
const FOLLOWUP_STYLE = {
  1: "bg-amber-50 text-amber-800 border-amber-200",
  2: "bg-orange-50 text-orange-800 border-orange-200",
  3: "bg-red-50 text-red-700 border-red-200",
};

function FollowupBadge({ stage, days }) {
  if (!stage) return null;
  return (
    <span title={`Contacted ${days} day${days === 1 ? "" : "s"} ago`} className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${FOLLOWUP_STYLE[stage]} inline-flex items-center gap-1`}>
      <Bell size={10} /> {FOLLOWUP_LABEL[stage]}
    </span>
  );
}

// ==========================================================
// Import Modal
// ==========================================================
function ImportModal({ onClose, onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dedupe, setDedupe] = useState("email");
  const [result, setResult] = useState(null);

  const run = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/leads/import?dedupe_by=${dedupe}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.inserted} lead${data.inserted === 1 ? "" : "s"} imported · ${data.skipped} skipped`);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full rounded-lg shadow-xl p-5" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Upload size={18} className="text-brand"/> Import leads</h3>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <p className="text-sm text-[color:var(--aii-text-2)]">
          Upload an Excel (.xlsx / .xls) or CSV file with columns like <b>Name, Age, Gender, Email, Phone, Country, Interested Program, Source</b>. Column names are matched case-insensitively — variations like &ldquo;Full Name&rdquo;, &ldquo;Mobile&rdquo;, &ldquo;Programme&rdquo; work automatically.
        </p>
        <label className="block mt-4">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Skip duplicates by</div>
          <select value={dedupe} onChange={(e) => setDedupe(e.target.value)} className="input-aii w-full" data-testid="dedupe-select">
            <option value="email">Email address</option>
            <option value="phone">Phone number</option>
            <option value="none">Don&rsquo;t skip — import everything</option>
          </select>
        </label>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={run} data-testid="file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pick-file"
          className="mt-4 w-full py-3 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <Upload size={14}/>}
          {busy ? "Importing…" : "Choose file"}
        </button>
        {result && (
          <div className="mt-4 text-sm space-y-1" data-testid="import-result">
            <div>Inserted: <b>{result.inserted}</b> · Skipped: <b>{result.skipped}</b></div>
            <div className="text-xs text-[color:var(--aii-muted)]">Mapped: {Object.entries(result.mapping || {}).map(([k, v]) => `${k}=${v}`).join(", ")}</div>
            {result.errors?.length > 0 && (
              <details className="text-xs text-red-700 mt-2"><summary>{result.errors.length} error(s)</summary>
                <ul className="pl-4 list-disc mt-1">{result.errors.map((e) => <li key={`row-${e.row}-${e.error?.slice(0,20)}`}>Row {e.row}: {e.error}</li>)}</ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================================
// Intro preview + send modal
// ==========================================================
function IntroModal({ lead, onClose, onSent }) {
  const [copy, setCopy] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("Your next adventure in India · Aii Group");

  useEffect(() => {
    api.get(`/leads/${lead.id}/preview-intro`)
      .then(({ data }) => setCopy(data.body || ""))
      .catch(() => toast.error("Could not generate preview — you can still edit manually"))
      .finally(() => setLoading(false));
  }, [lead.id]);

  const send = async () => {
    if (!copy.trim()) { toast.error("Body is empty"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/introductory-email`, { subject_override: subject, body_override: copy });
      toast.success(`Sent · id ${(data.email_id || "").slice(0, 8)}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="intro-modal">
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-center justify-between">
          <div>
            <div className="text-xs text-[color:var(--aii-muted)]">Introductory email · AI copy</div>
            <div className="font-semibold">{lead.name} · <span className="text-[color:var(--aii-muted)] font-normal">{lead.email || "no email"}</span></div>
          </div>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3 flex-1 overflow-y-auto">
          <label className="block">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Subject</div>
            <input className="input-aii w-full" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="intro-subject"/>
          </label>
          <label className="block">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Body — edit anything before you send</div>
            {loading ? (
              <div className="p-6 flex items-center gap-2 text-[color:var(--aii-muted)]"><Loader2 size={14} className="animate-spin"/> Generating with Claude…</div>
            ) : (
              <textarea className="input-aii w-full font-mono text-sm" rows={14} value={copy} onChange={(e) => setCopy(e.target.value)} data-testid="intro-body"/>
            )}
          </label>
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">Cancel</button>
          <button data-testid="intro-send" onClick={send} disabled={sending || loading || !lead.email}
            className="text-sm px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-2 disabled:opacity-50">
            {sending ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>}
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================================
// Lead form (add / edit)
// ==========================================================
function LeadForm({ initial, config, onCancel, onSave }) {
  const emptyForm = { name: "", age: "", gender: "", email: "", phone: "", country: "", interested_program: "", stage: "new", source: "", notes: "" };
  const [f, setF] = useState(initial || emptyForm);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    const payload = { ...f, age: f.age ? Number(f.age) : null };
    await onSave(payload);
    setBusy(false);
  };
  return (
    <div className="aii-card p-5 space-y-4" data-testid="lead-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2">
          {initial?.id ? <Pencil size={16} /> : <Plus size={16} />} {initial?.id ? "Edit lead" : "Add lead"}
        </h3>
        <button onClick={onCancel}><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block sm:col-span-2"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Name *</div><input className="input-aii w-full" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="l-name"/></label>
        <label className="block"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Age</div><input type="number" min="0" className="input-aii w-full" value={f.age || ""} onChange={(e) => setF({ ...f, age: e.target.value })}/></label>
        <label className="block"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Gender</div><input className="input-aii w-full" value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}/></label>
        <label className="block"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Email</div><input className="input-aii w-full" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="l-email"/></label>
        <label className="block"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Phone</div><input className="input-aii w-full" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}/></label>
        <label className="block"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Country</div><input className="input-aii w-full" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })}/></label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Interested program</div>
          <select className="input-aii w-full" value={f.interested_program} onChange={(e) => setF({ ...f, interested_program: e.target.value })}>
            <option value="">— select —</option>
            {config.programs.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Source</div><input className="input-aii w-full" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="e.g., Instagram, Website"/></label>
        <label className="block">
          <div className="text-xs text-[color:var(--aii-muted)] mb-1">Stage</div>
          <select className="input-aii w-full" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>
            {config.stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="block sm:col-span-2"><div className="text-xs text-[color:var(--aii-muted)] mb-1">Notes</div><textarea rows={2} className="input-aii w-full" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/></label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="l-save" className="text-sm px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5 disabled:opacity-50">
          <Save size={13}/> {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ==========================================================
// Marketing upload links manager
// ==========================================================
function UploadLinksModal({ onClose }) {
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState("Marketing Team");
  const [dedupe, setDedupe] = useState("email");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/upload-tokens");
    setTokens(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await api.post("/upload-tokens", { label: label.trim(), default_dedupe: dedupe });
      toast.success("Share link created");
      setLabel("Marketing Team");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this link? Anyone using it will lose access immediately.")) return;
    await api.delete(`/upload-tokens/${id}`);
    toast.success("Revoked");
    await load();
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Could not copy — select the URL manually"); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] rounded-lg shadow-xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="upload-links-modal">
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Shareable</div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><LinkIcon size={18} className="text-brand"/> Marketing upload links</h2>
            <p className="text-xs text-[color:var(--aii-text-2)] mt-1">Bookmark a link for the Marketing team to drop their weekly leads sheet without needing a login.</p>
          </div>
          <button onClick={onClose}><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          {/* Create */}
          <div className="aii-card p-4">
            <div className="text-sm font-semibold mb-2">Create a new share link</div>
            <div className="flex flex-wrap gap-2">
              <input placeholder="Label — e.g. Marketing Team" value={label} onChange={(e) => setLabel(e.target.value)}
                className="input-aii flex-1 min-w-[180px]" data-testid="ul-label"/>
              <select value={dedupe} onChange={(e) => setDedupe(e.target.value)} className="input-aii">
                <option value="email">Dedupe by email</option>
                <option value="phone">Dedupe by phone</option>
                <option value="none">No dedupe</option>
              </select>
              <button onClick={create} disabled={busy || !label.trim()} data-testid="ul-create"
                className="text-sm px-3 py-2 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1 disabled:opacity-50">
                <Plus size={13}/> Create
              </button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-2">
            {tokens.length === 0 && (
              <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No share links yet.</div>
            )}
            {tokens.map((t) => (
              <div key={t.id} data-testid={`ul-row-${t.id}`}
                className={`aii-card p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 ${t.is_active ? "" : "opacity-60"}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{t.label}</span>
                    {t.is_active ? (
                      <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Active</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">Revoked</span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--aii-muted)] mt-1 flex flex-wrap gap-x-3">
                    <span>{t.use_count} upload{t.use_count === 1 ? "" : "s"}</span>
                    <span>{t.leads_added} leads added</span>
                    <span>Created {new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-xs font-mono mt-1 break-all text-[color:var(--aii-text-2)]">{t.share_url}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {t.is_active && (
                    <>
                      <button onClick={() => copy(t.share_url)} data-testid={`ul-copy-${t.id}`}
                        className="text-xs px-2 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                        <Copy size={12}/> Copy
                      </button>
                      <a href={t.share_url} target="_blank" rel="noreferrer"
                        className="text-xs px-2 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                        Open
                      </a>
                      <button onClick={() => revoke(t.id)} data-testid={`ul-revoke-${t.id}`} title="Revoke"
                        className="text-xs px-2 py-1.5 border border-red-200 text-red-700 rounded hover:bg-red-50 flex items-center gap-1">
                        <Ban size={12}/> Revoke
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================================
// Main page
// ==========================================================
export default function Leads() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState({ stages: [], programs: [] });
  const [stageFilter, setStageFilter] = useState("all");
  const [programFilter, setProgramFilter] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [uploadLinksOpen, setUploadLinksOpen] = useState(false);
  const [introLead, setIntroLead] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const [{ data: r }, { data: s }, { data: cfg }] = await Promise.all([
      api.get("/leads"), api.get("/leads/summary"), api.get("/leads/config"),
    ]);
    setRows(r); setSummary(s); setConfig(cfg);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (stageFilter !== "all" && r.stage !== stageFilter) return false;
    if (programFilter !== "all" && (r.interested_program || "—") !== programFilter) return false;
    return true;
  }), [rows, stageFilter, programFilter]);

  const followupDue = useMemo(() => rows.filter((r) => r.followup_due > 0), [rows]);

  const saveLead = async (f) => {
    try {
      if (editing?.id) { await api.patch(`/leads/${editing.id}`, f); toast.success("Updated"); }
      else { await api.post("/leads", f); toast.success("Lead added"); }
      setShowForm(false); setEditing(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`); toast.success("Deleted"); await load();
  };
  const setStage = async (id, stage) => {
    await api.patch(`/leads/${id}`, { stage });
    toast.success(`Moved to ${stage}`);
    await load();
  };
  const markFollowedUp = async (id) => {
    await api.post(`/leads/${id}/followup-sent`);
    toast.success("Follow-up logged");
    await load();
  };

  const programsSet = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => s.add(r.interested_program || "—"));
    return Array.from(s).sort();
  }, [rows]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Marketing pipeline</div>
          <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2"><Users className="text-brand"/> Leads</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Import from Marketing, send warm intro emails, and nurture every lead through the funnel.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="btn-upload-links" onClick={() => setUploadLinksOpen(true)} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"><LinkIcon size={14}/> Marketing links</button>
          <button data-testid="btn-import" onClick={() => setImportOpen(true)} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"><Upload size={14}/> Import Excel/CSV</button>
          <button data-testid="btn-add-lead" onClick={() => { setEditing(null); setShowForm(true); }} className="text-sm px-3 py-2 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5"><Plus size={14}/> Add lead</button>
        </div>
      </div>

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
          <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Total</div><div className="text-2xl font-display font-semibold">{summary.total}</div></div>
          {config.stages.map((s) => (
            <div key={s} className="aii-card p-3">
              <div className="text-[color:var(--aii-muted)] capitalize">{s}</div>
              <div className="text-2xl font-display font-semibold">{summary.by_stage[s] || 0}</div>
            </div>
          ))}
          <div className="aii-card p-3"><div className="text-[color:var(--aii-muted)]">Conversion</div><div className="text-2xl font-display font-semibold text-brand">{summary.conversion_rate}%</div></div>
        </div>
      )}

      {/* Follow-up strip */}
      {followupDue.length > 0 && (
        <div className="aii-card p-4 border-l-4 border-amber-500" data-testid="lead-followup-strip">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="text-amber-600" size={18}/>
            <div>
              <div className="font-display font-semibold">{followupDue.length} lead{followupDue.length === 1 ? "" : "s"} due for follow-up</div>
              <div className="text-xs text-[color:var(--aii-muted)]">Contacted 3+ days ago and still nurturing — send a quick nudge.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {followupDue.map((l) => (
              <div key={l.id} className="flex items-start justify-between gap-2 p-2 rounded border border-[color:var(--aii-border)] bg-white">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate flex items-center gap-2">{l.name} <FollowupBadge stage={l.followup_due} days={l.days_since_contact}/></div>
                  <div className="text-xs text-[color:var(--aii-muted)] flex items-center gap-1 mt-0.5"><Clock size={10}/> {l.days_since_contact}d ago · {l.country || "—"} · {l.interested_program || "—"}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {l.phone && <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${l.name.split(" ")[0]} — following up on our earlier note from Aii Group. Any questions I can help with?`)}`} target="_blank" rel="noreferrer" onClick={() => markFollowedUp(l.id)} className="text-[10px] px-2 py-1 bg-emerald-600 text-white rounded flex items-center justify-center gap-1"><MessageCircle size={10}/> WhatsApp</a>}
                  {l.email && <a href={`mailto:${l.email}?subject=${encodeURIComponent("Following up · Aii Group")}`} onClick={() => markFollowedUp(l.id)} className="text-[10px] px-2 py-1 border border-[color:var(--aii-border)] rounded flex items-center justify-center gap-1"><Mail size={10}/> Email</a>}
                  <button onClick={() => markFollowedUp(l.id)} className="text-[10px] px-2 py-1 text-[color:var(--aii-muted)] hover:text-brand">Mark done</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && <LeadForm initial={editing} config={config} onCancel={() => { setShowForm(false); setEditing(null); }} onSave={saveLead}/>}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Filter size={13} className="text-[color:var(--aii-muted)]"/>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="input-aii" data-testid="stage-filter">
          <option value="all">All stages</option>
          {config.stages.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)} className="input-aii" data-testid="program-filter">
          <option value="all">All programs</option>
          {programsSet.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="text-[color:var(--aii-muted)] ml-1">Showing <b>{filtered.length}</b> / {rows.length}</span>
      </div>

      {/* Table */}
      <div className="aii-card overflow-x-auto">
        <table className="aii-table">
          <thead>
            <tr>
              <th>Name</th><th>Age/Gender</th><th>Email</th><th>Phone</th><th>Country</th><th>Program</th><th>Stage</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[color:var(--aii-muted)]">No leads. Import a spreadsheet or add one manually.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`lead-row-${r.id}`}>
                <td>
                  <div className="font-medium flex items-center gap-2">{r.name} <FollowupBadge stage={r.followup_due} days={r.days_since_contact}/></div>
                  {r.source && <div className="text-xs text-[color:var(--aii-muted)]">via {r.source}</div>}
                </td>
                <td className="text-xs">{r.age || "—"}{r.gender ? ` · ${r.gender}` : ""}</td>
                <td>{r.email ? <a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a> : <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                <td>{r.phone ? <a href={`https://wa.me/${r.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline flex items-center gap-1"><MessageCircle size={11}/> {r.phone}</a> : <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                <td>{r.country || "—"}</td>
                <td className="text-xs">{r.interested_program || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                <td>
                  <select value={r.stage} onChange={(e) => setStage(r.id, e.target.value)} className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border-0 ${STAGE_STYLES[r.stage] || ''}`} data-testid={`stage-${r.id}`}>
                    {config.stages.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {r.email && (
                      <button data-testid={`intro-${r.id}`} title="Send intro email" onClick={() => setIntroLead(r)} className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark inline-flex items-center gap-1"><Wand2 size={11}/> Intro</button>
                    )}
                    <button data-testid={`edit-${r.id}`} title="Edit" onClick={() => { setEditing(r); setShowForm(true); }} className="p-1 text-[color:var(--aii-muted)] hover:text-brand"><Pencil size={13}/></button>
                    <button data-testid={`del-${r.id}`} title="Delete" onClick={() => del(r.id)} className="p-1 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={load}/>}
      {uploadLinksOpen && <UploadLinksModal onClose={() => setUploadLinksOpen(false)}/>}
      {introLead && <IntroModal lead={introLead} onClose={() => setIntroLead(null)} onSent={load}/>}
    </div>
  );
}
